import type { ApiResponse } from '@aiinterview/shared';

/** Carries the field-keyed errors so a form can attach them to inputs. */
export class ApiError extends Error {
    constructor(
        message: string,
        readonly statusCode: number,
        readonly errors: Record<string, string[]> | null
    ) {
        super(message);
        this.name = 'ApiError';
    }

    /** The first message for a field, for rendering under the input. */
    fieldError(field: string): string | undefined {
        return this.errors?.[field]?.[0];
    }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
    const response = await fetch(path, {
        ...init,
        credentials: 'same-origin',
        headers: {
            // Only for a JSON string body. FormData must set its own content-type, because the
            // multipart boundary is generated by the browser and the server cannot parse the parts
            // without it.
            ...(typeof init?.body === 'string' ? { 'content-type': 'application/json' } : {}),
            ...init?.headers
        }
    });

    // 204 is a real answer on the speech route, not an error, and it has no body to parse.
    if (response.status === 204) {
        return null as T;
    }

    const body = await response.json().catch(() => null) as ApiResponse<T> | null;

    if (!body?.success) {
        // A 401 means the cookie is gone or the token was revoked. Routing here rather than in every
        // caller is what stops a revoked session firing twelve error toasts at once.
        if (response.status === 401 && !window.location.pathname.startsWith('/login')) {
            window.location.assign('/login');
        }

        throw new ApiError(
            body?.message ?? 'Something went wrong. Please try again.',
            response.status,
            body?.errors ?? null);
    }

    return body.data as T;
}

export const api = {
    get: <T>(path: string) => request<T>(path),

    post: <T>(path: string, payload?: unknown) =>
        request<T>(path, {
            method: 'POST',
            body: payload === undefined ? undefined : JSON.stringify(payload)
        }),

    /** Sends one file as multipart. The server identifies it by content, not by name or type. */
    upload: <T>(path: string, file: File) => {
        const form = new FormData();
        form.append('file', file);
        return request<T>(path, { method: 'POST', body: form });
    }
};
